import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from '@hooks/useParams';
import { endOfDay, startOfDay, subMonths } from 'date-fns';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { OnSortColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { DateRangeType } from '@dynamika-shared/ui/DatePicker/DateRangePicker';
import { NotificationTypes } from '@dynamika-shared/types/subscription';
import {
  setCurrentPage,
  useMakePagination,
} from '@restore/paginationAndSort/paginationAndSort.slice';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useClinicalTrialNotificationHistorySlice } from '@restore/clinicalTrialNotificationHistory/clinicalTrialNotificationHistory.slice';
import { NotificationDeliveryStatus } from '@restore/clinicalTrialNotificationHistory/clinicalTrialNotificationHistory.types';
import { makeNotificationHistoryColumns } from './NotificationHistory.columns';
import { NotificationHistoryFilters } from './NotificationHistoryFilters';
import { useNotificationHistoryFilters } from './useNotificationHistoryFilters';

const DEFAULT_SORT = 'dispatchedAt,desc';
const getDefaultDateRange = (): DateRangeType => {
  const now = new Date();
  return [subMonths(now, 1), now];
};

// NOTE: The recipient filter is a single free-text input that routes to either
// recipientEmail or recipientName depending on whether the value looks like an
// email. Backend expects the two as separate query params. A plain `includes`
// is used instead of a regex to avoid any chance of super-linear backtracking
// on user-controlled input — names don't contain '@', so the bare character
// check is a sufficient heuristic.
const isEmail = (value: string) => value.includes('@');

const toIsoInstant = (
  date: Date | null,
  edge: 'start' | 'end'
): string | undefined => {
  if (!date) return undefined;
  const normalised = edge === 'start' ? startOfDay(date) : endOfDay(date);
  return normalised.toISOString();
};

export const ClinicalTrialNotificationsHistory = () => {
  const dispatch = useDispatch();
  const { id: clinicalTrialId } = useParams();
  const [sortBy, setSortBy] = useState<string | undefined>(DEFAULT_SORT);
  const [dateRange, setDateRange] = useState<DateRangeType>(getDefaultDateRange);

  const clinicalTrial = useClinicalTrialSlice(state => state.data);

  const { data, loading, fetchNotificationHistory } =
    useClinicalTrialNotificationHistorySlice((state, actions) => ({
      data: state.data,
      loading: state.loading,
      fetchNotificationHistory: actions.fetchNotificationHistory,
    }));

  const {
    subFilters,
    filters,
    allFilters,
    toggleActiveFilter,
    toggleSelectedFilter,
    selectedFilters,
    filterOnClick,
    recipient,
    setRecipient,
  } = useNotificationHistoryFilters();

  const columns = useMemo(
    () =>
      makeNotificationHistoryColumns({
        clinicalTrialName: clinicalTrial?.name,
      }),
    [clinicalTrial?.name]
  );

  const recipientValue = recipient?.trim();
  const recipientIsEmail = !!recipientValue && isEmail(recipientValue);

  const notificationType = (selectedFilters.notificationType?.[0] ?? undefined) as
    | NotificationTypes
    | undefined;
  const status = (selectedFilters.status?.[0] ?? undefined) as
    | NotificationDeliveryStatus
    | undefined;

  const requestParams = useMemo(
    () => ({
      clinicalTrialId: clinicalTrialId ?? '',
      sort: sortBy,
      notificationType,
      status,
      recipientName:
        recipientValue && !recipientIsEmail ? recipientValue : undefined,
      recipientEmail: recipientIsEmail ? recipientValue : undefined,
      dispatchedFrom: toIsoInstant(dateRange[0], 'start'),
      dispatchedTo: toIsoInstant(dateRange[1], 'end'),
    }),
    [
      clinicalTrialId,
      sortBy,
      notificationType,
      status,
      recipientValue,
      recipientIsEmail,
      dateRange,
    ]
  );

  // Reset to page 1 before refetching so the user doesn't land on a stale
  // page index after the result set shrinks from a filter/sort change.
  useEffect(() => {
    if (!clinicalTrialId) return;
    dispatch(setCurrentPage({ listName: 'notificationHistory', newPage: 1 }));
    fetchNotificationHistory(requestParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId, requestParams]);

  const handlePaginationChange = useCallback(() => {
    if (!clinicalTrialId) return;
    fetchNotificationHistory(requestParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId, requestParams]);

  const onSort: OnSortColumnType = (columnName, sortState) => {
    setSortBy(sortState ? `${columnName},${sortState}` : undefined);
  };

  const pagination = useMakePagination(
    'notificationHistory',
    !!data?.totalElements,
    handlePaginationChange
  );

  return (
    <div className='flex flex-col gap-2 pt-3'>
      <NotificationHistoryFilters
        date={dateRange}
        onDateChange={setDateRange}
        subFilters={subFilters}
        allFilters={allFilters}
        selectedFilters={selectedFilters}
        filters={filters}
        recipient={recipient}
        onRecipientChange={setRecipient}
        onToggleSelectedFilter={toggleSelectedFilter}
        onToggleActiveFilter={toggleActiveFilter}
        onFilterClick={filterOnClick}
      />
      <Table
        columns={columns}
        data={data?.content ?? []}
        totalItems={data?.totalElements ?? 0}
        loading={loading}
        onSort={onSort}
        sortState={sortBy}
        pagination={pagination}
        styles={{
          table:
            (data?.content?.length ?? 0) > 0
              ? 'table-auto'
              : 'table-auto !min-w-0 w-fit',
        }}
        noContentMessage={
          <NoContent
            iconName={'team'}
            title={
              <span className='text-1424 text-spanishGray'>
                No{' '}
                <span className='font-semibold text-chineseBlack'>
                  Notifications
                </span>{' '}
                sent yet!
              </span>
            }
          >
            <></>
          </NoContent>
        }
      />
    </div>
  );
};
